import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => useContext(CurrencyContext);

// Rates are relative to BHD, the store's base pricing unit — prices entered in the
// admin panel (and stored in the DB) are BHD amounts. rate = how many units of that
// currency equal 1 BHD, so BHD itself must stay 1 (no conversion when displaying BHD).
export const CURRENCIES = {
  BHD: { code: 'BHD', symbol: 'BHD', symbolAr: 'د.ب', nameAr: 'دينار بحريني', nameEn: 'Bahraini Dinar', rate: 1, flag: '🇧🇭' },
  USD: { code: 'USD', symbol: '$', symbolAr: '$', nameAr: 'دولار أمريكي', nameEn: 'US Dollar', rate: 2.65, flag: '🇺🇸' },
  SAR: { code: 'SAR', symbol: 'SAR', symbolAr: 'ر.س', nameAr: 'ريال سعودي', nameEn: 'Saudi Riyal', rate: 9.97, flag: '🇸🇦' },
  AED: { code: 'AED', symbol: 'AED', symbolAr: 'د.إ', nameAr: 'درهم إماراتي', nameEn: 'UAE Dirham', rate: 9.77, flag: '🇦🇪' },
  KWD: { code: 'KWD', symbol: 'KWD', symbolAr: 'د.ك', nameAr: 'دينار كويتي', nameEn: 'Kuwaiti Dinar', rate: 0.82, flag: '🇰🇼' },
  QAR: { code: 'QAR', symbol: 'QAR', symbolAr: 'ر.ق', nameAr: 'ريال قطري', nameEn: 'Qatari Riyal', rate: 9.68, flag: '🇶🇦' },
  OMR: { code: 'OMR', symbol: 'OMR', symbolAr: 'ر.ع', nameAr: 'ريال عماني', nameEn: 'Omani Rial', rate: 1.02, flag: '🇴🇲' },
};

// A fixed BHD amount (e.g. a shipping or packaging fee quoted in BHD) is already in the
// store's base pricing unit, so this is an identity conversion — kept as a named helper
// so call sites stay clear about what unit they're passing in.
export const bhdToBase = (bhdAmount) => bhdAmount / CURRENCIES.BHD.rate;

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('hjr_currency');
    return saved && CURRENCIES[saved] ? saved : 'BHD';
  });

  useEffect(() => {
    localStorage.setItem('hjr_currency', currency);
  }, [currency]);

  const convert = (basePrice) => {
    const rate = CURRENCIES[currency]?.rate || 1;
    return basePrice * rate;
  };

  const format = (basePrice, langOverride) => {
    const value = convert(basePrice);
    const c = CURRENCIES[currency] || CURRENCIES.BHD;
    const lang = langOverride || localStorage.getItem('hjr_lang') || 'ar';
    const symbol = lang === 'ar' ? c.symbolAr : c.symbol;
    // Format according to currency decimals
    const decimals = ['KWD', 'BHD', 'OMR'].includes(currency) ? 3 : 2;
    const formatted = value.toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    // For SAR/AED/etc put symbol before amount in AR usually reads "ر.س 100"
    return `${symbol} ${formatted}`;
  };

  // Always formats in BHD (the store's actual pricing/accounting unit) regardless of the
  // shopper-facing currency currently selected. The admin dashboard/orders/reports/products
  // pages use this instead of `format` — an admin switching the storefront currency to
  // preview it as a customer shouldn't also see their own revenue figures converted to USD.
  const formatBase = (basePrice, langOverride) => {
    const c = CURRENCIES.BHD;
    const lang = langOverride || localStorage.getItem('hjr_lang') || 'ar';
    const symbol = lang === 'ar' ? c.symbolAr : c.symbol;
    const formatted = (basePrice || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-US', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
    return `${symbol} ${formatted}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convert, format, formatBase, currencies: CURRENCIES }}>
      {children}
    </CurrencyContext.Provider>
  );
};
